/**
 * Exports — the store's generated export files.
 *
 * The recovery path for the async export rail. An export too large to answer
 * inside a request becomes an ExportJob generated on the pipeline rail; the
 * button that asked for it polls that one job and downloads it inline, but
 * only while its tab is open. A large inventory export takes minutes, so a
 * merchant closing the tab, sleeping the laptop or simply navigating away is
 * the ordinary case. Without this page the finished file sits in a private
 * container nobody can reach until housekeeping deletes it.
 *
 * The filename comes from the SERVER, never rebuilt here: an artifact is
 * dated on the merchant's clock at GENERATION time and stays downloadable for
 * a week, so a name built from today would stamp Monday's export with
 * Wednesday's date (backend: core/models.py ExportJob.filename).
 *
 * A plain table rather than an AG Grid: the list is a handful of short-lived
 * rows with no sorting, filtering or column preferences to keep, and a grid
 * would cost the whole per-tab grid-state machinery for nothing.
 */

import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PageBanner from '../components/common/PageBanner';

import Tile from '../components/layout/Tile';
import { sessionApi } from '../services/SessionApiService';
import { apiErrorFrom } from '../utils/parseApiError';
import { translateRequestError } from '../queries/api';
import { showToast } from '../components/common/appToastBus';
import { downloadBlobResponse } from '../utils/download';
import { useFormatters } from '../hooks/useFormatters';
import { useExportJobs } from '../hooks/useExportJobs';
import { useInFlight } from '../hooks/useInFlight';
import { isGenerating, formatFileSize } from '../utils/exportJobs';
import '../styles/pages.css';

export default function Exports() {
  const { t } = useTranslation();
  const { formatNumber, formatDateTime } = useFormatters();
  // `isPending`, not `isLoading`. The query is gated on a selected store, and
  // a DISABLED query reports isLoading false with no data — which would fall
  // through to "no exports are waiting", claiming an empty list for a request
  // that was never made. Same false-empty as the failed-load case below, by a
  // different door.
  const { data, isPending, isError } = useExportJobs();
  // Per-row, not per-page: downloading one export must not disable the others.
  const { running: downloading, track } = useInFlight();

  const download = async (job) => {
    try {
      await track(job.id, 'download', async () => {
        const res = await sessionApi.get(`/api/export-jobs/${job.id}/download/`);
        if (!res.ok) throw await apiErrorFrom(res, 'GET /api/export-jobs/<id>/download/');
        await downloadBlobResponse(res, job.filename);
      });
    } catch (err) {
      // Caught to SURFACE, not to continue: a rejected fetch in a click
      // handler is not caught by React, so without this the merchant presses
      // Download, the button re-enables, and nothing else ever happens.
      console.error('export download failed:', err.message);
      showToast(translateRequestError(err, t, 'export.page.downloadFailed'), 'error');
    }
  };

  // The hook walks every page, so this is the whole list or the query failed
  // — there is no third state where it is silently the first page of several.
  const jobs = data ?? [];

  const statusLabel = (job) => {
    if (isGenerating(job)) return t('export.page.statusPreparing');
    return job.is_ready ? t('export.page.statusReady') : t('export.page.statusFailed');
  };

  return (
    <div className="page-container">
      <PageBanner severity="error" messages={[isError && t('export.page.loadFailed')]} />

      <Tile>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('export.page.subtitle')}
        </Typography>

        {/* "No exports" is a claim about the list, so it is made only when we
            actually have one. On a failed load the alert above is the whole
            answer — saying the list is empty would state as fact something the
            request never returned. */}
        {isPending ? (
          <CircularProgress />
        ) : isError ? null : jobs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{t('export.page.empty')}</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('export.page.columns.export')}</TableCell>
                  <TableCell>{t('export.page.columns.format')}</TableCell>
                  <TableCell align="right">{t('export.page.columns.size')}</TableCell>
                  <TableCell>{t('export.page.columns.requested')}</TableCell>
                  <TableCell>{t('export.page.columns.availableUntil')}</TableCell>
                  <TableCell>{t('export.page.columns.status')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    {/* The entity's own name, not the raw key: `inventory` is a
                        wire value, "Inventory" is what the merchant exported. */}
                    <TableCell>{t(`export.entity.${job.entity}`)}</TableCell>
                    <TableCell>{job.file_format.toUpperCase()}</TableCell>
                    <TableCell align="right">{formatFileSize(job.size_bytes, formatNumber)}</TableCell>
                    <TableCell>{formatDateTime(job.created_at)}</TableCell>
                    <TableCell>{formatDateTime(job.expires_at)}</TableCell>
                    <TableCell>{statusLabel(job)}</TableCell>
                    <TableCell align="right">
                      {job.is_ready && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={job.id in downloading}
                          onClick={() => download(job)}
                        >
                          {t('export.page.download')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Tile>
    </div>
  );
}
